import React from 'react';
import { Link } from 'react-router-dom';
import type { IconType } from 'react-icons';
import { IoFolder, IoHeart, IoLibrary, IoMusicalNote, IoMusicalNotes } from 'react-icons/io5';
import {
  BROWSE_FACETS,
  edgePresenceFilterHref,
  nodeFlagFilterHref,
  WESTERN_NOTES_EDGE,
} from '../browse/facets';
import { BrowseIcon, type BrowseAccent } from '../browse/BrowseIcon';
import { HAS_SVARAS_FLAG } from '../filters/definitions/flagFilter';
import { APP } from '../app/appConfig';

interface Tile {
  to: string;
  title: string;
  subtitle: string;
  icon: IconType;
  accent: BrowseAccent;
}

/*
 * h-full so a grid row's tiles match height whatever their subtitle wraps to —
 * without it the two-line subtitles leave ragged tiles next to one-line ones.
 */
const Card: React.FC<Tile> = ({ to, title, subtitle, icon, accent }) => (
  <Link
    to={to}
    className="group flex h-full items-center gap-4 rounded-2xl border border-light bg-bg-2/60 px-5 py-4 hover:bg-white/5 hover:border-strong transition-colors"
  >
    <BrowseIcon icon={icon} accent={accent} />
    <span className="min-w-0">
      <span className="block text-h3 font-display font-semibold text-text-primary">{title}</span>
      <span className="block text-small text-text-tertiary mt-1">{subtitle}</span>
    </span>
  </Link>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-h3 font-display font-semibold mb-4">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </section>
);

const Browse: React.FC = () => {
  const { bhajanBabiesSection, collections, svarasList, westernNotesList } = APP.features;
  const hasBrowseBy = BROWSE_FACETS.length > 0 || svarasList || westernNotesList;

  return (
    <main id="main" className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="text-h1 font-display font-semibold mb-8">Browse</h1>

      <Section title="Bhajans">
        {/* `library` in purple — native's "All Content" hero tile, same glyph. */}
        <Card to="/bhajans" title="All Bhajans" subtitle="Search and browse the full library" icon={IoLibrary} accent="purple" />
      </Section>

      {/*
        "Browse by" carries the facet hubs AND the two notation lists. The
        notation cards are direct /bhajans links with a pre-applied filter, not
        facet hubs with a value list, so they stay out of ALL_FACETS and are
        rendered by hand after the facets.

        Each notation card is gated on its own tenant flag, NOT on a live count
        of matching bhajans — see AppFeatures.svarasList for why the answer is
        declared rather than fetched. ss has neither, which is what makes the
        `hasBrowseBy` guard load-bearing: drop both cards from a tenant whose
        facet list is also empty and the section becomes a heading over an
        empty grid.
      */}
      {hasBrowseBy && (
        <Section title="Browse by">
          {BROWSE_FACETS.map((f) => (
            <Card key={f.key} to={`/browse/${f.key}`} title={f.pluralLabel} subtitle={`Bhajans by ${f.label.toLowerCase()}`} icon={f.icon} accent={f.accent} />
          ))}
          {/*
            Svaras and Western Notes carry native's glyphs unchanged: the plural
            `musical-notes` for svara notation, the singular `musical-note` for a
            staff score. Ragams above is the same singular note in purple — that
            pairing is native's too, and the accent is what tells them apart.
          */}
          {svarasList && (
            <Card
              to={nodeFlagFilterHref(HAS_SVARAS_FLAG)}
              title="Bhajans with Svaras"
              subtitle="Bhajans whose lyrics carry svara notation"
              icon={IoMusicalNotes}
              accent="purple"
            />
          )}
          {westernNotesList && (
            <Card
              to={edgePresenceFilterHref(WESTERN_NOTES_EDGE)}
              title="Bhajans with Western Notes"
              subtitle="Bhajans with a Western staff-notation score"
              icon={IoMusicalNote}
              accent="indigo"
            />
          )}
        </Section>
      )}

      {(bhajanBabiesSection || collections) && (
        <Section title="More">
          {bhajanBabiesSection && <Card to="/bhajan-babies" title="Bhajan Babies" subtitle="Animated cartoon bhajans on YouTube" icon={IoHeart} accent="gold" />}
          {collections && <Card to="/collections" title="Collections" subtitle="Curated collections" icon={IoFolder} accent="gold" />}
        </Section>
      )}
    </main>
  );
};

export default Browse;
